import { API_URL } from "@/constants";
import { capture } from "@/lib/posthog";

/**
 * Error codes returned by the backend gating layers. These come back on non-2xx
 * responses (e.g. 402/403 from `requireBetaAccess`, 403 from view gating) with an
 * `errorCode` field, which we read before the generic `response.ok` check below.
 */
export type GatingErrorCode =
  | "PENDING_REVIEW"
  | "SUBSCRIPTION_EXPIRED"
  | "NO_VIEWS_LEFT"
  | "LOCKED_CONTRACTING"
  // El plan no admite más líneas de negocio (`plan_maxsearchprofiles`).
  | "SEARCH_PROFILE_LIMIT"
  // Culqi rechazó el cobro; `message` trae el texto que Culqi redacta para el usuario.
  | "PAYMENT_FAILED"
  // Operando con llaves de prueba: solo los admins pueden contratar.
  | "PAYMENTS_DISABLED";

export interface BackendError {
  error?: string;
  errorCode?: GatingErrorCode | string;
  message?: string;
  status?: number;
}

export default async function fetchToBackend<T = any>(
  route: string,
  config?: RequestInit,
): Promise<T> {
  const response = await fetch(`${API_URL}/api${route}`, {
    ...config,
    headers: {
      "Content-Type": "application/json",
      ...config?.headers,
    },
    credentials: "include",
  });

  let data: any = null;
  try {
    data = await response.json();
  } catch {
    /* empty body */
  }

  // Some gating responses come back as 200 but carry an errorCode.
  if (data && typeof data === "object" && "errorCode" in data) {
    // Único punto por el que pasan los 7 códigos de gating: medir aquí cubre
    // toda la fricción de planes sin instrumentar cada pantalla.
    capture("gating_error_shown", { code: data.errorCode, route });
    throw { ...data, status: response.status } as BackendError;
  }

  if (!response.ok) {
    throw { ...(data ?? {}), status: response.status } as BackendError;
  }

  return data as T;
}

/**
 * Build a query string from a record of filter values.
 * - Arrays are joined with commas (matching the Angular backend contract).
 * - Empty strings, null, undefined and empty arrays are skipped.
 */
export function buildQuery(
  params: Record<
    string,
    string | number | boolean | (string | number)[] | null | undefined
  >,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(","));
    } else {
      search.set(key, String(value));
    }
  }
  const str = search.toString();
  return str ? `?${str}` : "";
}
